import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FiInfo } from 'react-icons/fi';
import { FaSearch } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Reports.css';

function Reports() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');

      try {
        // Get Property Manager name
        const res = await fetch(
          `https://api.rside.app/PropertyManagerName?email=${user.email}`
        );
        const data = await res.json();

        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        // Get all properties
        const propRes = await fetch(
          `https://api.rside.app/GetAllProperties?full_name=${encodeURIComponent(
            data.Full_Name
          )}&Email=${encodeURIComponent(user.email)}`
        );

        if (!propRes.ok) {
          const err = await propRes.text();
          console.error('GetAllProperties failed:', err);
          return;
        }

        const propData = await propRes.json();

        // Group by Property_ID to avoid duplicates for multi-family
        const propertiesMap = new Map();
        for (const p of propData.Properties || []) {
          const propId = p.Property_ID;
          if (!propertiesMap.has(propId)) {
            propertiesMap.set(propId, {
              id: propId,
              name: p.Property_Name,
              propertyType:
                parseInt(p.Num_Of_Units) > 1
                  ? 'Multi Family Home'
                  : 'Single Family Home',
              numberOfUnits: p.Num_Of_Units,
              status: p.Status || 'Vacant',
              address: p.Street_Address,
              units: []
            });
          }
          propertiesMap.get(propId).units.push(p.Status || 'Vacant');
        }

        // Calculate overall status for multi-family
        const mapped = Array.from(propertiesMap.values()).map((prop) => {
          if (parseInt(prop.numberOfUnits) > 1 && prop.units.length > 1) {
            const occupied = prop.units.filter(s => s === 'Occupied' || s === 'Occupied').length;
            const vacant = prop.units.filter(s => s === 'Vacant').length;
            if (occupied === prop.units.length) {
              prop.status = 'Occupied';
            } else if (vacant === prop.units.length) {
              prop.status = 'Vacant';
            } else {
              prop.status = 'Partial';
            }
          }
          return prop;
        });

        setProperties(mapped.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (err) {
        console.error('Error loading properties:', err);
      }
    });

    return unsub;
  }, [navigate]);

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'vacant': return 'status-vacant';
      case 'occupied':
      case 'Occupied': return 'status-Occupied';
      case 'partial': return 'status-partial';
      default: return 'status-vacant';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'vacant': return 'Vacant';
      case 'occupied':
      case 'Occupied': return 'Occupied';
      case 'partial': return 'Partial';
      default: return 'Vacant';
    }
  };

  // Filter properties based on search term
  const filteredProperties = properties.filter(property =>
    property.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="home-page">
      <Sidebar initials={initials} />

      <div className="main-content">
        <Navbar title="Reports" />

        <div className="reports-container">
          {/* Info Banner */}
          <div className="reports-info-banner">
            <FiInfo className="info-icon" />
            <div className="info-text">
              <span className="info-title">Select a property to show a Year-To-Date Report.</span>
              <span className="info-subtitle">Reports will show data at a property level. All units under a multifamily home will be included under a single report</span>
            </div>
          </div>

          {/* Property Cards for Reports */}
          {filteredProperties.length > 0 ? (
            <div className="reports-cards-grid">
              {filteredProperties.map((property, index) => (
                <div
                  key={property.id}
                  className="report-card"
                  onClick={() => navigate(`/reports/${property.id}`)}
                >
                  <div className="report-card-header">
                    <div className="report-card-number">
                      <span className="report-number-label">No.</span>
                      <span className="report-number-value">{index + 1}.</span>
                    </div>
                    <span className={`report-status ${getStatusClass(property.status)}`}>
                      {getStatusLabel(property.status)}
                    </span>
                  </div>

                  <div className="report-card-name-section">
                    <span className="report-name-value">{property.name}</span>
                  </div>

                  <div className="report-card-body">
                    <div className="report-info-row">
                      <div className="report-info-item">
                        <span className="report-info-label">Property Type</span>
                        <span className="report-info-value">{property.propertyType}</span>
                      </div>
                      <div className="report-info-item">
                        <span className="report-info-label">No. of units</span>
                        <span className="report-info-value">{property.numberOfUnits}</span>
                      </div>
                    </div>
                    <div className="report-address">
                      <span className="report-info-label">Address</span>
                      <span className="report-info-value">{property.address}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-reports">
              <p>No properties available for reports</p>
              <button onClick={() => navigate('/properties/add')}>
                Add a Property First
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Reports;